import React, { useState } from 'react';
import GraphqlTable from 'cccisd-graphql-table';
import platformRenders from 'cccisd-table-renders';
import UploadImage from '../../../renders/UploadImage';
import studentInfoQuery from './studentInfo.graphql';
import studentProgressQuery from './studentProgress.graphql';
import classProgressQuery from './classProgress.graphql';
import schoolProgressQuery from './schoolProgress.graphql';
import style from './style.css';

const {
    html: { Audio, Image },
} = platformRenders;

const Appdefs = window.cccisd.appDefs.pawn;
const Fortress = window.cccisd.fortress;

function StudentTab({ renderTimepointPills, currTimepoint }) {
    const roleHandle = Fortress.user.acting.role.handle;
    const [currView, setCurrView] = useState('info');

    function uppercase({ value }) {
        return typeof value === 'string' ? <span>{value.charAt(0).toUpperCase() + value.slice(1)}</span> : value;
    }

    const studentCommonColumns = [
        {
            name: 'fields.studentId',
            label: 'Student ID',
            sort: true,
            filter: true,
        },
        {
            name: 'user.firstName',
            label: 'First Name',
            sort: true,
            filter: true,
        },
        {
            name: 'user.lastName',
            label: 'Last Name',
            sort: true,
            filter: true,
        },
        {
            name: 'ancestorGroups.class.group.label',
            label: 'Class',
            sort: true,
            filter: true,
        },
    ];

    if (
        ['uberadmin', 'guAdmin'].includes(roleHandle) &&
        !studentCommonColumns.find(col => col.name === 'ancestorGroups.site.group.label')
    ) {
        studentCommonColumns.splice(3, 0, {
            name: 'ancestorGroups.site.group.label',
            label: 'School',
            sort: true,
            filter: true,
        });
    }

    let query = studentInfoQuery;
    let columns = [];
    let rowKey = 'pawn.pawnId';
    switch (currView) {
        case 'info':
            columns = [
                ...studentCommonColumns,
                {
                    label: 'Grade',
                    name: 'fields.grade',
                    render: ({ value }) => <span>{value === '0' ? 'K' : value}</span>,
                    filter: true,
                    filterSettings: {
                        type: 'selectbox',
                        options: Appdefs.fields
                            .find(f => f.handle === 'grade')
                            .values.map(f => ({ label: f.name, value: f.value })),
                    },
                    sort: true,
                },
                {
                    label: 'Birthdate',
                    name: 'fields.birthdate',
                    filter: true,
                    filterSettings: {
                        type: 'date',
                    },
                    sort: true,
                },
                {
                    label: 'Gender',
                    name: 'fields.gender',
                    render: uppercase,
                    filter: true,
                    filterSettings: {
                        type: 'selectbox',
                        options: Appdefs.fields
                            .find(f => f.handle === 'gender')
                            .values.map(f => ({ label: f.name, value: f.value })),
                    },
                    sort: true,
                },
                {
                    label: 'Ethnicity',
                    name: 'fields.ethnicity',
                    render: uppercase,
                    filter: true,
                    filterSettings: {
                        type: 'selectbox',
                        options: Appdefs.fields
                            .find(f => f.handle === 'ethnicity')
                            .values.map(f => ({ label: f.name, value: f.value })),
                    },
                    sort: true,
                },
                {
                    label: 'Audio',
                    name: 'fields.pn_audio_url',
                    render: renderProps =>
                        renderProps.value && Audio({ audioUrlPath: 'fields.pn_audio_url' })(renderProps),
                    sort: true,
                    filter: false,
                },
                {
                    label: 'Image',
                    name: 'fields.pn_image_url',
                    render: renderProps =>
                        renderProps.value && Image({ imageUrlPath: 'fields.pn_image_url' })(renderProps),
                    sort: true,
                    filter: false,
                },
                {
                    label: 'Upload',
                    name: 'imageUpload',
                    hideInCsv: true,
                    render: renderProps => UploadImage()(renderProps),
                    sort: false,
                    filter: false,
                },
            ];
            break;

        case 'progress_by_student':
            query = studentProgressQuery;
            columns = [
                ...studentCommonColumns,
                {
                    label: `Time ${currTimepoint}: Status`,
                    name: 'status',
                    filter: true,
                    filterSettings: {
                        type: 'selectbox',
                        options: [
                            {
                                label: 'Not Started',
                                value: 'Not Started',
                            },
                            {
                                label: 'In Progress',
                                value: 'In Progress',
                            },
                            {
                                label: 'Complete',
                                value: 'Complete',
                            },
                        ],
                    },
                    sort: true,
                },
                {
                    label: `Time ${currTimepoint}: Started`,
                    name: 'assignmentProgress.startedDate',
                    filter: true,
                    filterSettings: {
                        type: 'date',
                    },
                    sort: true,
                    width: '120px',
                    className: 'text-center',
                },
                {
                    handle: 'completed',
                    label: `Time ${currTimepoint}: Completed`,
                    name: 'assignmentProgress.completedDate',
                    filter: true,
                    filterSettings: {
                        type: 'date',
                    },
                    sort: true,
                    width: '120px',
                    className: 'text-center',
                },
            ];
            break;

        case 'progress_by_class':
            query = classProgressQuery;
            rowKey = 'group.groupId';
            columns = [
                {
                    label: 'Class',
                    name: 'group.label',
                    filter: true,
                    sort: true,
                },
                {
                    label: 'Teacher',
                    name: 'targetedByPawns.instructorList',
                    render: ({ value }) =>
                        value && (
                            <ul className={style.instructorList}>
                                {value.map(teacher => (
                                    <li key={teacher.user.fullName}>{teacher.user.fullName}</li>
                                ))}
                            </ul>
                        ),
                    filter: true,
                    filterSettings: {
                        type: 'text',
                        field: 'targetedByPawns.instructor.user.fullName',
                    },
                    sort: false,
                },
                {
                    label: 'Active Students',
                    name: 'descendantRoles.total',
                    filter: false,
                    sort: true,
                    className: 'text-center',
                },
                {
                    label: `Time ${currTimepoint}: Started`,
                    name: 'descendantRoles.started',
                    filter: false,
                    sort: true,
                    className: 'text-center',
                },
                {
                    label: `Time ${currTimepoint}: Completed`,
                    name: 'descendantRoles.completed',
                    filter: false,
                    sort: true,
                    className: 'text-center',
                },
                {
                    label: `Time ${currTimepoint}: % Completed`,
                    name: 'percent',
                    render: ({ row }) => {
                        try {
                            let isTimepointAssigned = row.selectedAssignmentPlan.some(
                                assignmentPlan =>
                                    assignmentPlan.role === 'learner' &&
                                    assignmentPlan.sessionList.some(session => session.timepoint === currTimepoint)
                            );
                            if (!isTimepointAssigned) {
                                return <span>Not assigned</span>;
                            }
                        } catch (e) {
                            // do nothing, it will just display 0's instead of "Not assigned", which is usually appropriate
                        }

                        const totalLearners = row['descendantRoles.total'];
                        const completedLearners = row['descendantRoles.completed'];
                        let percent = Math.round((completedLearners / totalLearners) * 100);
                        if (totalLearners === 0) {
                            percent = 0;
                        }

                        return <span>{percent}%</span>;
                    },
                    filter: false,
                    sort: false,
                    className: 'text-center',
                },
            ];
            if (
                ['uberadmin', 'guAdmin'].includes(roleHandle) &&
                !columns.find(col => col.name === 'ancestorGroups.site.group.label')
            ) {
                columns.splice(1, 0, {
                    name: 'ancestorGroups.site.group.label',
                    label: 'School',
                    sort: true,
                    filter: true,
                });
            }
            break;

        case 'progress_by_school':
            rowKey = 'group.groupId';
            query = schoolProgressQuery;
            columns = [
                {
                    label: 'School',
                    name: 'group.label',
                    filter: true,
                    sort: true,
                },
                {
                    label: 'Active Students',
                    name: 'descendantRoles.total',
                    filter: false,
                    sort: true,
                    className: 'text-center',
                },
                {
                    label: `Time ${currTimepoint}: Started`,
                    name: 'descendantRoles.started',
                    filter: false,
                    sort: true,
                    className: 'text-center',
                },
                {
                    label: `Time ${currTimepoint}: Completed`,
                    name: 'descendantRoles.completed',
                    filter: false,
                    sort: true,
                    className: 'text-center',
                },
                {
                    label: `Time ${currTimepoint}: % Completed`,
                    name: 'percent',
                    render: ({ row }) => {
                        const totalLearners = row['descendantRoles.total'];
                        const completedLearners = row['descendantRoles.completed'];
                        let percent = Math.round((completedLearners / totalLearners) * 100);
                        if (totalLearners === 0) {
                            percent = 0;
                        }

                        return <span>{percent}%</span>;
                    },
                    filter: false,
                    sort: false,
                    className: 'text-center',
                },
            ];
            break;

        default:
            break;
    }

    let viewOptions = [
        {
            name: 'Student Information',
            handle: 'info',
        },
        {
            name: 'Student Progress',
            handle: 'progress_by_student',
        },
        {
            name: 'Student Progress by Class',
            handle: 'progress_by_class',
        },
    ];

    if (['uberadmin', 'guAdmin'].includes(roleHandle)) {
        viewOptions.push({
            name: 'Student Progress by School',
            handle: 'progress_by_school',
        });
    }

    return (
        <div>
            {renderTimepointPills()}
            <select
                className={'form-select d-inline-block ' + style.viewPicker}
                id="students_view"
                name="students_view"
                value={currView}
                onChange={e => setCurrView(e.target.value)}
            >
                {viewOptions.map(opt => (
                    <option value={opt.handle} key={opt.handle}>
                        {opt.name}
                    </option>
                ))}
            </select>
            <GraphqlTable
                key={currView === 'info' ? 'info' : currView + currTimepoint}
                isStickyHeader
                showDoubleScrollbar
                rowKey={rowKey}
                query={query}
                columns={columns}
                graphqlVariables={{ timepoint: currTimepoint }}
            />
        </div>
    );
}

export default StudentTab;
